// PopTip — positions and hover-wires the tap-to-reveal tips rendered by
// CoreComponents.tip/1 (and cap_threshold_line): a `[popovertarget]` button
// next to a `[popover]` panel, both inside the hooked element.
//
// Touch: the native popover handles tap-to-toggle and light dismiss. Mouse:
// hover shows it; a click while hovering pins it. The panel lives in the top
// layer, so we position it ourselves (no anchor positioning yet on Android).

// Pure: place the panel under the trigger, centred, clamped to the viewport;
// flip above when there is no room below.
export function popoverPosition(anchor, pop, viewport, gap = 8) {
  let left = anchor.left + anchor.width / 2 - pop.width / 2
  let top = anchor.bottom + gap

  left = Math.max(8, Math.min(left, viewport.width - pop.width - 8))
  if (top + pop.height > viewport.height - 8) {
    top = Math.max(8, anchor.top - pop.height - gap)
  }

  return {left, top}
}

const safe = (fn) => {
  try {
    return fn()
  } catch (_e) {
    return undefined
  }
}

export default {
  mounted() {
    const wrap = this.el
    const btn = wrap.querySelector("[popovertarget]")
    const pop = wrap.querySelector("[popover]")
    if (!btn || !pop || typeof pop.showPopover !== "function") return

    const isOpen = () => pop.matches(":popover-open")
    const place = () => {
      const {left, top} = popoverPosition(
        btn.getBoundingClientRect(),
        pop.getBoundingClientRect(),
        {width: window.innerWidth, height: window.innerHeight}
      )
      pop.style.left = `${left}px`
      pop.style.top = `${top}px`
    }

    let shownByHover = false
    let pinned = false

    pop.addEventListener("toggle", (e) => {
      if (e.newState === "open") {
        place()
        requestAnimationFrame(place)
      } else {
        shownByHover = false
        pinned = false
      }
    })

    // A tap on the trigger must not also fire the row's phx-click.
    btn.addEventListener("click", (e) => {
      e.stopPropagation()
      if (shownByHover && isOpen()) {
        // Hover already opened it — a click pins it instead of toggling it shut.
        e.preventDefault()
        shownByHover = false
        pinned = true
      }
    })

    if (window.matchMedia("(hover: hover) and (pointer: fine)").matches) {
      let timer
      btn.addEventListener("mouseenter", () => {
        timer = setTimeout(() => {
          if (!isOpen()) {
            shownByHover = true
            safe(() => pop.showPopover())
          }
        }, 100)
      })
      wrap.addEventListener("mouseleave", () => {
        clearTimeout(timer)
        if (!pinned && isOpen()) safe(() => pop.hidePopover())
      })
    }

    // Scrolling the page underneath detaches the panel from its trigger: hide.
    this.onScroll = (e) => {
      if (e.target === pop || pop.contains(e.target)) return
      if (isOpen()) safe(() => pop.hidePopover())
    }
    window.addEventListener("scroll", this.onScroll, {capture: true, passive: true})
  },

  destroyed() {
    if (this.onScroll) window.removeEventListener("scroll", this.onScroll, {capture: true})
  },
}
